/**
 * State behind the Invite drawer: the form, its link target, the imported-name
 * suggestions and the submit.
 */
import { useEffect, useMemo, useState } from 'react';

import type { UserRow } from '../userViewModel';

import {
  detectMatch,
  findImportedNameMatches,
  isFormValid,
  needsRegions,
  splitName,
  type InviteFormState,
} from './invite-form-model';
import type { EmailMatch } from './InviteEmailField';
import { useInviteSubmit } from './use-invite-submit';

interface UseInviteFormArgs {
  existingUsers: UserRow[];
  prefillFrom?: UserRow;
  onInvited?: () => void;
}

export function useInviteForm({ existingUsers, prefillFrom, onInvited }: UseInviteFormArgs) {
  const [form, setForm] = useState<InviteFormState>({
    email: prefillFrom?.email ?? '',
    ...splitName(prefillFrom?.name ?? ''),
    roleIds: [],
    regionIds: prefillFrom?.regions.map((r) => r.id) ?? [],
  });
  const [pickedImport, setPickedImport] = useState<UserRow | null>(null);

  const linkTarget = prefillFrom ?? pickedImport;
  const match = useMemo<EmailMatch>(
    () =>
      linkTarget ? { kind: 'backfill', user: linkTarget } : detectMatch(form.email, existingUsers),
    [form.email, existingUsers, linkTarget],
  );

  // Keyed on the row itself so typing in the email field does not overwrite a
  // name the operator has corrected.
  const linkedUser = match.kind === 'backfill' ? match.user : null;
  useEffect(() => {
    if (!linkedUser) return;
    setForm((f) => ({
      ...f,
      ...splitName(linkedUser.name),
      regionIds: linkedUser.regions.map((r) => r.id),
    }));
  }, [linkedUser]);

  const { firstName, lastName, email } = form;
  const nameMatches = useMemo(
    () =>
      match.kind === 'none'
        ? findImportedNameMatches({ firstName, lastName, email }, existingUsers)
        : [],
    [match.kind, firstName, lastName, email, existingUsers],
  );

  const requiresRegion = needsRegions(form.roleIds);
  const isValid = isFormValid(form, match, requiresRegion);

  const { submit, submitting, errorMessage, successMessage, confirmLinkExisting, linkNotice } =
    useInviteSubmit(form, match, onInvited);

  return {
    form,
    setForm,
    match,
    nameMatches,
    pickImport: setPickedImport,
    /** Set only when the operator picked the link target themselves. */
    clearPicked: pickedImport && !prefillFrom ? () => setPickedImport(null) : undefined,
    requiresRegion,
    confirmLinkExisting,
    linkNotice,
    canSubmit: isValid && !submitting && !successMessage,
    submitting,
    errorMessage,
    successMessage,
    submit,
  };
}
